import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, ChevronDown, Droplets, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { userApiService } from "../services/userApiService";

const DAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

const DURATION_OPTIONS = [
  { value: 60, label: "1 min" },
  { value: 120, label: "2 min" },
  { value: 300, label: "5 min" },
  { value: 600, label: "10 min" },
  { value: 900, label: "15 min" },
  { value: 1800, label: "30 min" },
];

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      type="button"
      disabled={disabled}
      className={`relative h-7 w-12 rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-accent" : "bg-muted"}`}
    >
      <span className={`absolute top-1 h-5 w-5 rounded-full bg-card shadow transition-all ${checked ? "left-6" : "left-1"}`} />
    </button>
  );
}

function formatLastRun(lastRunAt, lastSkipReason) {
  if (!lastRunAt) return "Never run";
  const when = new Date(lastRunAt).toLocaleString();
  if (lastSkipReason) return `Last attempt ${when} (skipped: ${lastSkipReason.replace(/_/g, " ")})`;
  return `Last run ${when}`;
}

function daysLabel(days = []) {
  if (!days.length || days.length === 7) return "Every day";
  const map = Object.fromEntries(DAY_OPTIONS.map((d) => [d.value, d.label]));
  return days.map((d) => map[d] || d).join(", ");
}

export default function IrrigationScheduleEditor({ deviceId, deviceLabel = "", disabled = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: "Morning irrigation",
    time: "06:00",
    days: [1, 2, 3, 4, 5, 6],
    durationSec: 300,
    skipIfSoilAbove: "",
    enabled: true,
  });

  const loadSchedules = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const data = await userApiService.listIrrigationSchedules(deviceId);
      setSchedules(Array.isArray(data?.items) ? data.items : []);
    } catch {
      toast.error("Failed to load irrigation schedules");
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  function toggleDay(day) {
    setForm((prev) => {
      const has = prev.days.includes(day);
      const days = has ? prev.days.filter((d) => d !== day) : [...prev.days, day].sort((a, b) => a - b);
      return { ...prev, days: days.length ? days : [day] };
    });
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!deviceId || disabled) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        skipIfSoilAbove: form.skipIfSoilAbove === "" ? null : Number(form.skipIfSoilAbove),
      };
      const data = await userApiService.createIrrigationSchedule(deviceId, payload);
      setSchedules((prev) => [...prev, data.item].sort((a, b) => String(a.time).localeCompare(String(b.time))));
      setShowForm(false);
      toast.success("Schedule saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(schedule) {
    if (disabled) return;
    setSaving(true);
    try {
      const data = await userApiService.updateIrrigationSchedule(deviceId, schedule.scheduleId, {
        enabled: !schedule.enabled,
      });
      setSchedules((prev) => prev.map((s) => (s.scheduleId === schedule.scheduleId ? data.item : s)));
    } catch {
      toast.error("Failed to update schedule");
      await loadSchedules();
    } finally {
      setSaving(false);
    }
  }

  async function removeSchedule(schedule) {
    if (disabled) return;
    setSaving(true);
    try {
      await userApiService.deleteIrrigationSchedule(deviceId, schedule.scheduleId);
      setSchedules((prev) => prev.filter((s) => s.scheduleId !== schedule.scheduleId));
      toast.success("Schedule removed");
    } catch {
      toast.error("Failed to delete schedule");
    } finally {
      setSaving(false);
    }
  }

  const enabledCount = schedules.filter((s) => s.enabled).length;
  const summary = loading
    ? "Loading…"
    : schedules.length
      ? `${enabledCount} active · ${schedules.length} total`
      : "No schedules";

  return (
    <div className="rounded-2xl border border-border bg-cream/30 min-w-0 w-full box-border overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-cream/50 transition-colors"
        aria-expanded={open}
      >
        <CalendarClock className="h-4 w-4 shrink-0 text-accent" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">Scheduled irrigation</div>
          <div className="text-xs text-muted-foreground truncate">{summary}</div>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-border px-4 pb-4 pt-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="text-xs text-muted-foreground max-w-md">
              Auto-run the pump for {deviceLabel || deviceId} at set times (Asia/Kolkata). Device must be online.
            </p>
            {!disabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowForm((v) => !v)}
                disabled={saving || loading}
              >
                <Plus className="h-4 w-4" />
                Add schedule
              </Button>
            )}
          </div>

          {loading ? (
            <p className="mt-3 text-sm text-muted-foreground">Loading schedules…</p>
          ) : (
            <>
              {showForm && !disabled && (
                <form onSubmit={handleCreate} className="mt-3 grid gap-3 rounded-xl border border-border bg-card p-4">
                  <label className="grid gap-1 text-sm">
                    <span className="text-muted-foreground">Label</span>
                    <input
                      className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                      value={form.label}
                      onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                      maxLength={80}
                    />
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-1 text-sm">
                      <span className="text-muted-foreground">Time</span>
                      <input
                        type="time"
                        className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                        value={form.time}
                        onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                        required
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span className="text-muted-foreground">Duration</span>
                      <select
                        className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                        value={form.durationSec}
                        onChange={(e) => setForm((f) => ({ ...f, durationSec: Number(e.target.value) }))}
                      >
                        {DURATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Days</div>
                    <div className="flex flex-wrap gap-2">
                      {DAY_OPTIONS.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleDay(day.value)}
                          className={`rounded-full px-3 py-1 text-xs border ${form.days.includes(day.value) ? "bg-accent text-accent-foreground border-accent" : "bg-card border-border"}`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="grid gap-1 text-sm">
                    <span className="text-muted-foreground">Skip if soil above % (optional)</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="e.g. 60 — leave empty to always run"
                      className="rounded-xl border border-input bg-cream/50 px-3 py-2"
                      value={form.skipIfSoilAbove}
                      onChange={(e) => setForm((f) => ({ ...f, skipIfSoilAbove: e.target.value }))}
                    />
                  </label>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving}>
                      Save schedule
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-3 space-y-3">
                {!schedules.length && (
                  <p className="text-sm text-muted-foreground">
                    No schedules yet. Add one to water automatically — uses the same pump command as manual control.
                  </p>
                )}
                {schedules.map((schedule) => (
                  <div
                    key={schedule.scheduleId}
                    className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <Droplets className="h-4 w-4 text-accent" />
                        {schedule.label || "Scheduled run"}
                        {!schedule.enabled && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide">
                            Paused
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {schedule.time} · {daysLabel(schedule.days)} ·{" "}
                        {Math.round((schedule.durationSec || 300) / 60)} min
                        {schedule.skipIfSoilAbove != null
                          ? ` · skip if soil > ${schedule.skipIfSoilAbove}%`
                          : ""}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {formatLastRun(schedule.lastRunAt, schedule.lastSkipReason)}
                      </div>
                    </div>
                    {!disabled && (
                      <div className="flex items-center gap-3">
                        <Toggle
                          checked={schedule.enabled}
                          onChange={() => toggleEnabled(schedule)}
                          disabled={saving}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSchedule(schedule)}
                          disabled={saving}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
